import { flow, Instance, SnapshotIn, types } from "mobx-state-tree";
import { externalEvents } from "@closerplatform/closer-sdk";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { servicesGetters } from "../utils";
import { partition } from "lodash";
import { ArtichokePresence, UserRole } from "@closerplatform/spinner-openapi";
import { isSupportedCountry, parsePhoneNumber } from "libphonenumber-js";

const browserDataKeys = [
  "browserName",
  "browserVersion",
  "currentPage",
  "screenSize",
  "browserSize",
  "operatingSystem",
  "currentPageTitle"
];

export const Phone = types.model("Phone", {
  region: types.string,
  number: types.string
});

export const BackOfficeField = types.model("BackOfficeField", {
  key: types.string,
  value: types.frozen(),
  displayName: types.maybe(types.string)
});

export const Guest = types
  .model("Guest", {
    id: types.identifier,
    roomId: types.string,
    firstName: types.maybe(types.string),
    lastName: types.maybe(types.string),
    randomName: types.maybe(types.string),
    email: types.maybe(types.string),
    phone: types.maybe(Phone),
    status: types.optional(
      types.enumeration("PresenceStatus", Object.values(ArtichokePresence)),
      ArtichokePresence.Unavailable
    ),
    tags: types.array(types.string),
    backOfficeData: types.array(BackOfficeField),
    additionalData: types.array(BackOfficeField),
    browserData: types.array(BackOfficeField),
    languageLocale: types.maybe(types.string),
    zoneId: types.maybe(types.string),
    location: types.maybe(types.string),
    role: UserRole.LEAD,
    deletedAt: types.maybe(types.integer),
    tagGroupId: types.maybe(types.string),
    topics: types.array(types.string),
    accessToken: types.maybe(types.string)
  })
  .preProcessSnapshot((snapshot) => {
    // Phone as a user defined prototype cannot be converted into mobx property smoothly - convert it
    // into Object.prototype to satisfy mobx then.
    if (snapshot && snapshot.phone && snapshot.phone.constructor !== Object) {
      snapshot.phone = { ...snapshot.phone };
    }
    return snapshot;
  })
  .views(servicesGetters)
  .views((self) => {
    const getFullName = () => `${self.firstName || ""} ${self.lastName || ""}`.trim();
    const getAnonymousLabel = (id: string) =>
      intl.formatMessage("user.displayedName.anonymousClient", { id });
    const views = {
      /** Agent role check */
      get isAgent(): boolean {
        return false;
      },
      /** Guest role check */
      get isGuest(): boolean {
        return true;
      },
      get hasContactData(): boolean {
        return !!self.email || !!self.phone;
      },
      get hasName(): boolean {
        return !!self.firstName || !!self.lastName;
      },
      // TODO: deprecate in favor of / refactor to displayedName
      get displayName(): string | undefined {
        return views.displayedName;
      },
      get fullName(): string {
        return getFullName();
      },
      // TODO: unification with Agent.displayedName
      get displayedName(): string | undefined {
        return getFullName() || self.randomName;
      },
      get fakeFirstName(): string {
        return self.randomName ? self.randomName.split(" ")[0]! : getAnonymousLabel(self.id);
      },
      get fakeLastName(): string {
        return self.randomName ? self.randomName.split(" ")[1]! : getAnonymousLabel(self.id);
      },
      get phoneNumber(): string | undefined {
        const phoneRegion =
          self.phone && isSupportedCountry(self.phone.region) ? self.phone.region : undefined;
        const phoneNumber = self.phone
          ? parsePhoneNumber(self.phone.number, {
              defaultCountry: phoneRegion
            }).formatInternational()
          : "";

        return self.phone && phoneNumber;
      }
    };
    return views;
  })
  .actions((self) => ({
    setTopics(topicNames: string[]) {
      self.topics.replace(topicNames);
    },
    setStatus(status: ArtichokePresence) {
      self.status = status;
    },
    update: flow(function* update(data: Partial<SnapshotIn<typeof self>>, persist?: boolean) {
      if (data.id && data.id !== self.id) {
        throw new Error("The guestId is being changed during update - forbidden operation.");
      }

      const [additionalData, browserData] = partition(
        data.backOfficeData || [],
        (field) => browserDataKeys.find((key) => key === field.key) === undefined
      );

      if (persist) {
        try {
          yield self.spinnerService.patchGuestProfile(self.id, data);
        } catch (error) {
          logger.error({ message: "Coudln't update guest profile", error });
          throw new Error("Count not update guest profile");
        }
      }
      Object.assign(self, {
        ...data,
        phone: (data.phone && { ...data.phone }) || undefined,
        additionalData,
        browserData
      });
    }),
    delete: flow(function* () {
      try {
        yield self.spinnerService.deleteGuestProfile(self.id);
        const newData = yield self.spinnerService.getGuestProfile(self.id);
        Object.assign(self, { ...newData });
      } catch (error) {
        logger.error({ message: "Couldn't delete guest profile", error });
        throw new Error("Could not delete guest profile");
      }
    }),
    // TODO: deprecate in favor of update
    updateFromEvent(event: externalEvents.GuestProfileUpdated) {
      // it's event tag sadly so exclude from rest
      const { tag, locale, customerId, backOfficeData, tags, topics, tagGroupId, ...rest } = event;
      // tslint:disable-next-line: no-invalid-this
      this.update({
        ...rest,
        id: customerId,
        languageLocale: locale,
        backOfficeData: [...backOfficeData],
        tags: tags as string[], // readonly string[] -> string[]
        topics: topics as string[], // readonly string[] -> string[]
        tagGroupId
      });
    }
  }));

export type IGuest = Instance<typeof Guest>;
